import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

// Translation resources
const resources = {
  en: {
    translation: {
      // SEO Page Titles
        seo: {
          homepage: 'Sacred Wellness Sanctuary - Luxury Spa & Biohacking',
          treatments: 'Sacred Treatments - Massage, Facials, Biohacking & Wellness Rituals',
          contact: 'Contact Us - Book Your Sacred Wellness Experience',
          quiz: 'Sacred Wellness Quiz - Discover Your Perfect Treatment',
          blog: 'Sacred Wellness Wisdom - Holistic Health & Spa Blog',
          privacy: 'Privacy Policy - Your Sacred Trust & Data Protection',
          terms: 'Terms of Service - Sacred Guidelines & Spa Policies',
          booking: 'Book Treatment - Reserve Your Sacred Wellness Experience',
          virtualEscape: 'Virtual Escape - Sacred Meditation & Relaxation',
          treatmentDetail: 'Sacred Treatment Details - Professional Spa Services'
        },
        meta: {
          homepage: 'Discover sacred wellness at Ama Spa - luxury treatments, biohacking technology, and transformative experiences. Book your journey to inner balance and radiant health.',
          treatments: 'Explore our sacred treatment menu: CBD massages, chromotherapy facials, infrared saunas, and cutting-edge biohacking wellness rituals for mind, body & soul.',
          contact: 'Ready for your sacred wellness journey? Contact Ama Spa to book treatments, ask questions, or learn about our luxury wellness experiences in Spain.',
          quiz: 'Take our personalized wellness quiz to discover the perfect sacred treatments for your unique needs. Find your path to optimal health and inner radiance.',
          blog: 'Explore sacred wellness wisdom through our holistic health blog. Discover biohacking tips, spa rituals, meditation practices, and transformative wellness insights.',
          privacy: 'Your privacy is sacred to us. Learn how Ama Wellness & Spa protects your personal information and maintains the highest standards of data security.',
          terms: 'Review our terms of service for Ama Wellness & Spa. Understanding our sacred guidelines ensures the best possible wellness experience for all guests.',
          booking: 'Book your transformative wellness experience at Ama Spa. Choose from luxury treatments, biohacking sessions, and sacred rituals for complete rejuvenation.',
          virtualEscape: 'Experience sacred meditation and relaxation from anywhere. Our virtual escape offers guided wellness practices for inner peace and spiritual renewal.',
          treatmentDetail: 'Detailed information about our professional spa services and sacred wellness treatments. Learn about benefits, duration, and what to expect.'
        },
        og: {
          homepage: 'Sacred Wellness Sanctuary - Luxury Spa & Biohacking',
          treatments: 'Sacred Treatments - Massage, Facials, Biohacking & Wellness Rituals',
          contact: 'Contact Us - Book Your Sacred Wellness Experience',
          quiz: 'Sacred Wellness Quiz - Discover Your Perfect Treatment',
          blog: 'Sacred Wellness Wisdom - Holistic Health & Spa Blog',
          privacy: 'Privacy Policy - Your Sacred Trust & Data Protection',
          terms: 'Terms of Service - Sacred Guidelines & Spa Policies',
          booking: 'Book Treatment - Reserve Your Sacred Wellness Experience',
          virtualEscape: 'Virtual Escape - Sacred Meditation & Relaxation',
          treatmentDetail: 'Sacred Treatment Details - Professional Spa Services'
        },

      // Navigation
      nav: {
        home: 'Home',
        treatments: 'Treatments',
        wellnessQuiz: 'Wellness Quiz',
        aromatherapy: 'Aromatherapy',
        virtualEscape: 'Virtual Escape',
        blog: 'Journal',
        booking: 'Book Now',
        about: 'About',
        contact: 'Contact'
      },
      
      // Homepage
      hero: {
        title: 'Rediscover Your Inner Radiance',
        subtitle: 'Where nature, wellness, and technology merge beautifully',
        cta: 'Begin Your Journey',
        toggleSound: 'Toggle video sound'
      },
      
      // Treatments
      treatments: {
        title: 'Sacred Treatments',
        subtitle: 'Each treatment tells a story of transformation',
        massage: {
          title: 'Massage Therapy',
          swedish: 'Swedish Massage',
          deepTissue: 'Deep Tissue Massage',
          aromatherapy: 'Aromatherapy Massage',
          hotStone: 'Hot Stone Massage',
          reflexology: 'Reflexology',
          indianHead: 'Indian Head Massage'
        },
        facials: {
          title: 'Facials & Beauty',
          tailored: 'Tailored Facials',
          antiAging: 'Anti-aging Treatments',
          exfoliation: 'Full-body Exfoliations'
        },
        body: {
          title: 'Body & Ritual Therapies',
          wraps: 'Body Wraps',
          balinese: 'Balinese Rituals',
          hammam: 'Hammam Rituals',
          wellness: 'Wellness Rituals'
        },
        other: {
          title: 'Other Services',
          manicure: 'Manicure & Pedicure',
          waxing: 'Waxing',
          eyebrows: 'Eyebrow & Eyelash Services'
        }
      },
      
      // Wellness Quiz
      quiz: {
        title: 'What Does Your Soul Crave?',
        subtitle: 'Let us guide you to your perfect sacred treatment',
        resultsTitle: 'Your Sacred Path Revealed',
        resultsSubtitle: 'These treatments have been divinely selected for your journey',
        perfectTreatment: '✨ Your Perfect Treatment',
        alsoRecommended: '🌿 Also Recommended',
        bookTreatment: 'Book This Treatment',
        takeAgain: 'Take Quiz Again',
        questions: {
          energy: 'How is your energy flowing today?',
          focusArea: 'Where would you like to focus your healing?',
          experienceType: 'What type of sacred experience calls to you?'
        },
        options: {
          energy: {
            depleted: {
              title: 'Deeply Depleted',
              description: 'I feel drained and need complete restoration'
            },
            stressed: {
              title: 'Stress & Tension',
              description: 'My body holds stress, especially in neck and shoulders'
            },
            seekingBalance: {
              title: 'Seeking Balance',
              description: 'I want to harmonize and center myself'
            },
            needDetox: {
              title: 'Need Renewal',
              description: 'I want to cleanse and start fresh'
            }
          },
          focusArea: {
            fullBody: {
              title: 'Whole Body Sanctuary',
              description: 'Complete relaxation from head to toe'
            },
            faceGlow: {
              title: 'Radiant Face',
              description: 'Facial renewal and glowing skin'
            },
            muscleRelief: {
              title: 'Muscle Relief',
              description: 'Deep tissue work for tension and knots'
            },
            mindSpirit: {
              title: 'Mind & Spirit',
              description: 'Mental clarity and emotional release'
            }
          },
          experienceType: {
            traditional: {
              title: 'Ancient Wisdom',
              description: 'Traditional massage and natural treatments'
            },
            technology: {
              title: 'Advanced Biohacking',
              description: 'Cutting-edge technology for optimal wellness'
            },
            ritual: {
              title: 'Sacred Rituals',
              description: 'Multi-step ceremonies and body treatments'
            },
            quickEscape: {
              title: 'Express Renewal',
              description: 'Powerful results in shorter time'
            }
          }
        }
      },
      
      // Homepage
      homepage: {
        hero: {
          messages: [
            {
              title: "Rediscover Your Inner Radiance",
              subtitle: "Where nature, wellness, and technology merge beautifully"
            },
            {
              title: "Awaken Your Sacred Self", 
              subtitle: "Journey into the depths of healing and transformation"
            },
            {
              title: "Embrace Divine Wellness",
              subtitle: "Ancient wisdom meets modern luxury in every treatment"
            },
            {
              title: "Find Your Sacred Sanctuary",
              subtitle: "A haven where body, mind, and spirit unite in harmony"
            },
            {
              title: "Experience Celestial Healing",
              subtitle: "Let the universe guide your path to inner peace"
            },
            {
              title: "Illuminate Your Spirit",
              subtitle: "Discover the light that has always lived within you"
            }
          ],
          scroll: 'Discover'
        },
        features: {
          title: 'Your Sacred Journey Awaits',
          subtitle: 'Choose your path to wellness and transformation',
          treatments: {
            title: 'Sacred Treatments',
            description: 'Discover our complete range of healing treatments designed to restore your body, mind, and spirit',
            cta: 'Explore Treatments'
          },
          quiz: {
            title: 'Wellness Quiz',
            description: 'Take our sacred quiz to discover the perfect treatments for your unique wellness journey',
            cta: 'Start Your Quiz'
          },
          wisdom: {
            title: 'Wellness Wisdom',
            description: 'Discover sacred wellness knowledge, expert insights, and transformative practices for your journey',
            cta: 'Explore Wisdom'
          }
        }
      },

      // The Ama Experience Section
      amaExperience: {
        title: 'The Ama Experience',
        subtitle: 'Where holistic well-being meets advanced technology',
        philosophy: {
          text1: 'Discover a space of holistic well-being and advanced technology, where our philosophy is based on providing relaxation and stress relief in your daily life. We have elevated the concept of wellness by incorporating non-invasive technologies into our treatments, with highly effective therapies that deliver visible results from the very first session.',
          text2: 'An experience that transforms, renews, and leaves lasting benefits for both body and mind.'
        },
        imageGallery: {
          infraredTherapy: 'Infrared and red light therapy',
          treatmentRooms: 'Treatment Rooms',
          advancedTechnology: 'Advanced Technology',
          swimmingPool: 'Swimming Pool'
        },
        biohacking: {
          title: 'BIOHACKING & THERAPEUTIC',
          subtitle: 'EXPERIENCES',
          description: 'Advanced wellness rituals that combine technology, nature, and smart techniques to enhance energy, longevity, and holistic balance.',
          services: [
            {
              title: 'Full Spectrum Infrared Sauna',
              description: 'Deep penetrating heat therapy for detoxification, pain relief, and cellular regeneration'
            },
            {
              title: 'Bioenergetic Measurement',
              description: 'Advanced analysis of your body\'s energy fields and biorhythms for personalized wellness protocols'
            },
            {
              title: 'Body Ballancer Pressotherapy',
              description: 'Pneumatic compression therapy for enhanced circulation, lymphatic drainage, and recovery'
            },
            {
              title: 'Vibrational Sound Therapy',
              description: 'Therapeutic frequencies and vibrations to harmonize body, mind, and cellular healing'
            }
          ]
        },
        ballancer: {
          title: 'What sets the Ballancer®Pro apart?',
          features: [
            'Global leader in the market since 20 years',
            'Precisely graduated and evenly distributed pressure',
            'Exact calibration',
            'Maxed out at safe 80mmHg',
            'Backed by clinical studies',
            'Patented \'pre-treatment\' mode that prepares lymph nodes',
            'FDA cleared',
            'Full Body Recovery System'
          ]
        },
        bookNow: 'Book Now'
      },

      // Booking Modal
      bookingModal: {
        title: 'Book Your Spa Experience',
        sections: {
          treatment: {
            title: 'Select Treatment',
            label: 'Choose your treatment',
            placeholder: 'Select a treatment...',
            minutes: 'minutes'
          },
          datetime: {
            title: 'Preferred Date & Time',
            dateLabel: 'Preferred Date',
            timeLabel: 'Preferred Time',
            timePlaceholder: 'Select time...',
            timeOptions: {
              '09:00': '9:00 AM',
              '10:00': '10:00 AM',
              '11:00': '11:00 AM',
              '12:00': '12:00 PM',
              '13:00': '1:00 PM',
              '14:00': '2:00 PM',
              '15:00': '3:00 PM',
              '16:00': '4:00 PM',
              '17:00': '5:00 PM',
              '18:00': '6:00 PM'
            }
          },
          contact: {
            title: 'Contact Information',
            nameLabel: 'Full Name',
            emailLabel: 'Email Address',
            phoneLabel: 'Phone Number',
            requestsLabel: 'Special Requests or Notes',
            requestsPlaceholder: 'Any allergies, preferences, or special requirements...',
            firstTimeLabel: 'This is my first visit to Ama Wellness & Spa'
          }
        },
        buttons: {
          submit: 'Request Booking',
          submitting: 'Submitting...',
          close: 'Close'
        },
        validation: {
          requiredFields: '🌙 Please fill in all required fields for your sacred journey! 🌙',
          invalidEmail: '🌙 Please enter a valid email address for your sacred wellness journey! 🌙'
        },
        messages: {
          success: '🌙✨ Your sacred booking request has been submitted! You will receive a confirmation email shortly and we will contact you within 24 hours to confirm your appointment. ✨🌙',
          error: '💫 Sacred energies are temporarily disrupted. Please try again or call us directly at +34 610 76 02 78! 💫'
        }
      },

      // Contact
      contact: {
        hero: {
          title: 'Connect With Us',
          subtitle: 'Begin your sacred journey to wellness and transformation'
        },
        contactCards: {
          location: {
            title: 'Visit Our Sanctuary',
            description: 'Available for appointments and inquiries'
          },
          phone: {
            title: 'Call Us',
            description: 'Available for appointments and inquiries'
          },
          email: {
            title: 'Email Us',
            description: 'We\'ll respond within 24 hours'
          },
          instagram: {
            title: 'Follow Our Journey',
            description: 'Daily wellness inspiration & behind-the-scenes'
          }
        },
        map: {
          title: 'Find Our Sacred Sanctuary'
        },
        form: {
          title: 'Send Us a Message',
          subtitle: 'Let us help you discover your perfect wellness journey',
          labels: {
            name: 'Name *',
            email: 'Email *',
            phone: 'Phone',
            subject: 'Subject',
            message: 'Message *'
          },
          placeholders: {
            message: 'Tell us how we can help you on your wellness journey...'
          },
          buttons: {
            send: 'Send Message',
            sending: 'Sending...'
          }
        },
        validation: {
          requiredFields: '🌙 Please fill in all required fields for your sacred inquiry! 🌙',
          invalidEmail: '🌙 Please enter a valid email address for your sacred communication! 🌙',
          success: '🌙✨ Your sacred message has been sent! We will respond within 4 hours during business hours. Thank you for connecting with our sanctuary of healing! ✨🌙',
          error: '💫 Sacred energies are temporarily disrupted. Please try again or call us directly at +34 610 76 02 78! 💫'
        }
      },

      // Luna Chatbot
      luna: {
        placeholder: "Ask Luna about your sacred wellness journey...",
        poweredBy: "Powered by",
        clearChat: "Clear Chat",
        toggleChat: "Toggle Chat",
        errorMessage: "🌙 *Sacred energy disrupted...* ✨\n\nMy cosmic connection to the divine realm is temporarily blocked, beautiful soul. Please try again in a moment while I realign my celestial frequencies! 🔮💫",
        channeling: "🌙 Luna is channeling cosmic wisdom..."
      },

      // Common
      common: {
        loading: 'Loading...',
        readMore: 'Read More',
        bookNow: 'Book Now',
        learnMore: 'Learn More',
        close: 'Close',
        previous: 'Previous',
        next: 'Next',
        continue: 'Continue',
        discoverPath: 'Discover My Path',
        email: 'Email',
        phone: 'Phone',
        name: 'Name',
        message: 'Message',
        send: 'Send',
        subscribe: 'Subscribe'
      },

      // Sacred Moments Slider
      sacredMoments: {
        title: 'Sacred Moments',
        subtitle: 'Each image carries the essence of transformation',
        images: [
          {
            title: '5D Massage',
            subtitle: 'The future of relaxation.',
            meaning: 'Experience the next dimension of healing touch'
          },
          {
            title: 'Lunar Radiance',
            subtitle: 'A Face That Glows Like the Full Moon.',
            meaning: 'Illuminate your natural beauty from within'
          },
          {
            title: 'Botanical Body Detox Ritual',
            subtitle: 'As Light as a Whisper.',
            meaning: 'Gentle transformation that speaks to your soul'
          },
          {
            title: 'Deep Transformation',
            subtitle: 'Beneath the Surface, Everything Shifts.',
            meaning: 'True healing begins where the visible ends'
          },
          {
            title: 'Light Therapy Sanctuary',
            subtitle: 'Detox, Relax, and boost wellness with infrared and red light therapy.',
            meaning: 'Cellular rejuvenation through sacred light frequencies'
          },
          {
            title: 'Daily Sacred Ritual',
            subtitle: 'Every day is a great day for a spa day.',
            meaning: 'Embrace wellness as a way of life'
          },
          {
            title: 'Wisdom & Creation',
            subtitle: 'Leave with expert knowledge and two products made by you.',
            meaning: 'Empower your journey with handcrafted healing'
          },
          {
            title: 'Cellular Awakening',
            subtitle: 'Light That Awakens Your Cells.',
            meaning: 'Ignite your body\'s natural healing intelligence'
          },
          {
            title: 'Soul Renewal',
            subtitle: 'Like Shedding the Skin of the Soul.',
            meaning: 'Release what no longer serves your highest self'
          },
          {
            title: 'Mother & Daughter Ritual',
            subtitle: 'Moments That Stay Forever.',
            meaning: 'Sacred bonding through shared wellness experiences'
          },
          {
            title: 'Sacred Sound Healing',
            subtitle: 'Om Shanti Shanti Shanti Om.',
            meaning: 'Find peace through ancient vibrational wisdom'
          },
          {
            title: 'Inner Radiance Facial',
            subtitle: 'Renew your skin from within.',
            meaning: 'True beauty emerges from inner harmony'
          },
          {
            title: 'Sonic Healing',
            subtitle: 'The power of Tibetan bowls on the body.',
            meaning: 'Ancient frequencies that restore energetic balance'
          }
        ]
      },
      
      // Footer
      footer: {
        tagline: 'A return to yourself',
        newsletter: 'Sacred Newsletter',
        newsletterDesc: 'Receive wellness wisdom and exclusive offers',
        followUs: 'Follow Us',
        quickLinks: 'Quick Links',
        treatments: 'Treatments',
        policies: 'Policies',
        privacy: 'Privacy Policy',
        terms: 'Terms of Service',
        copyright: '© 2025 Ama Wellness & Spa. All rights reserved.',
        madeWith: 'Made with ❤️ <a href="https://emerv.com" target="_blank" rel="noopener noreferrer">by EmerV🌸</a> @ <a href="https://infinia.ie" target="_blank" rel="noopener noreferrer">Infinia</a>'
      },

      // Treatments Page
      treatmentsPage: {
        title: 'Sacred Treatments',
        subtitle: 'Relax and renew with holistic, biohacking therapies that reduce stress and improve body and mind from the very first session.',
        categories: {
          all: 'All Treatments',
          massages: 'Body Massages',
          rituals: 'Body Rituals',
          facials: 'Facials',
          packages: 'Face & Body',
          biohacking: 'Biohacking'
        },
        bookButton: 'Book This Journey'
      },

      // Blog Post Page
      blog: {
        backToBlog: 'Back to Blog',
        bookingCTA: 'Book Your Wellness Experience',
        relatedTopics: 'Related Topics',
        postNotFound: {
          title: 'Post Not Found',
          message: "The blog post you're looking for doesn't exist."
        }
      }
    }
  },
  
  es: {
    translation: {
      // SEO Page Titles - Spanish
        seo: {
          homepage: 'Santuario de Bienestar Sagrado - Spa de Lujo y Biohacking',
          treatments: 'Tratamientos Sagrados - Masajes, Faciales, Biohacking y Rituales de Bienestar',
          contact: 'Contacto - Reserva Tu Experiencia de Bienestar Sagrado',
          quiz: 'Quiz de Bienestar Sagrado - Descubre Tu Tratamiento Perfecto',
          blog: 'Sabiduría de Bienestar Sagrado - Blog de Salud Holística y Spa',
          privacy: 'Política de Privacidad - Tu Confianza Sagrada y Protección de Datos',
          terms: 'Términos de Servicio - Pautas Sagradas y Políticas del Spa',
          booking: 'Reservar Tratamiento - Reserva Tu Experiencia de Bienestar Sagrado',
          virtualEscape: 'Escape Virtual - Meditación Sagrada y Relajación',
          treatmentDetail: 'Detalles del Tratamiento Sagrado - Servicios Profesionales de Spa'
        },
        meta: {
          homepage: 'Descubre el bienestar sagrado en Ama Spa - tratamientos de lujo, tecnología biohacking y experiencias transformadoras. Reserva tu viaje hacia el equilibrio interior.',
          treatments: 'Explora nuestro menú de tratamientos sagrados: masajes CBD, faciales cromoterapia, saunas infrarrojas y rituales de bienestar de vanguardia para mente, cuerpo y alma.',
          contact: '¿Listo para tu viaje de bienestar sagrado? Contacta Ama Spa para reservar tratamientos, hacer preguntas o conocer nuestras experiencias de lujo en España.',
          quiz: 'Realiza nuestro quiz personalizado de bienestar para descubrir los tratamientos sagrados perfectos para tus necesidades únicas. Encuentra tu camino hacia la salud óptima.',
          blog: 'Explora la sabiduría del bienestar sagrado a través de nuestro blog de salud holística. Descubre consejos de biohacking, rituales de spa y prácticas transformadoras.',
          privacy: 'Tu privacidad es sagrada para nosotros. Conoce cómo Ama Wellness & Spa protege tu información personal y mantiene los más altos estándares de seguridad.',
          terms: 'Revisa nuestros términos de servicio de Ama Wellness & Spa. Entender nuestras pautas sagradas asegura la mejor experiencia de bienestar para todos los huéspedes.',
          booking: 'Reserva tu experiencia transformadora de bienestar en Ama Spa. Elige entre tratamientos de lujo, sesiones de biohacking y rituales sagrados para rejuvenecer completamente.',
          virtualEscape: 'Experimenta meditación sagrada y relajación desde cualquier lugar. Nuestro escape virtual ofrece prácticas de bienestar guiadas para la paz interior y renovación espiritual.',
          treatmentDetail: 'Información detallada sobre nuestros servicios profesionales de spa y tratamientos de bienestar sagrado. Conoce beneficios, duración y qué esperar.'
        },
        og: {
          homepage: 'Santuario de Bienestar Sagrado - Spa de Lujo y Biohacking',
          treatments: 'Tratamientos Sagrados - Masajes, Faciales, Biohacking y Rituales de Bienestar',
          contact: 'Contacto - Reserva Tu Experiencia de Bienestar Sagrado',
          quiz: 'Quiz de Bienestar Sagrado - Descubre Tu Tratamiento Perfecto',
          blog: 'Sabiduría de Bienestar Sagrado - Blog de Salud Holística y Spa',
          privacy: 'Política de Privacidad - Tu Confianza Sagrada y Protección de Datos',
          terms: 'Términos de Servicio - Pautas Sagradas y Políticas del Spa',
          booking: 'Reservar Tratamiento - Reserva Tu Experiencia de Bienestar Sagrado',
          virtualEscape: 'Escape Virtual - Meditación Sagrada y Relajación',
          treatmentDetail: 'Detalles del Tratamiento Sagrado - Servicios Profesionales de Spa'
        },

      // Navigation
      nav: {
        home: 'Inicio',
        treatments: 'Tratamientos',
        wellnessQuiz: 'Quiz de Bienestar',
        aromatherapy: 'Aromaterapia',
        virtualEscape: 'Escape Virtual',
        blog: 'Diario',
        booking: 'Reservar',
        about: 'Acerca',
        contact: 'Contacto'
      },
      
      // Homepage
      homepage: {
        hero: {
          messages: [
            {
              title: "Redescubre Tu Resplandor Interior",
              subtitle: "Donde la naturaleza, el bienestar y la tecnología se fusionan hermosamente"
            },
            {
              title: "Despierta Tu Ser Sagrado",
              subtitle: "Viaja hacia las profundidades de la sanación y transformación"
            },
            {
              title: "Abraza el Bienestar Divino",
              subtitle: "La sabiduría ancestral se encuentra con el lujo moderno en cada tratamiento"
            },
            {
              title: "Encuentra Tu Santuario Sagrado",
              subtitle: "Un refugio donde cuerpo, mente y espíritu se unen en armonía"
            },
            {
              title: "Experimenta la Sanación Celestial",
              subtitle: "Deja que el universo guíe tu camino hacia la paz interior"
            },
            {
              title: "Ilumina Tu Espíritu",
              subtitle: "Descubre la luz que siempre ha vivido dentro de ti"
            }
          ],
          scroll: 'Descubrir'
        },
        features: {
          title: 'Tu Viaje Sagrado Te Espera',
          subtitle: 'Elige tu camino hacia el bienestar y la transformación',
          treatments: {
            title: 'Tratamientos Sagrados',
            description: 'Descubre nuestra gama completa de tratamientos curativos diseñados para restaurar tu cuerpo, mente y espíritu',
            cta: 'Explorar Tratamientos'
          },
          quiz: {
            title: 'Quiz de Bienestar',
            description: 'Toma nuestro quiz sagrado para descubrir los tratamientos perfectos para tu viaje único de bienestar',
            cta: 'Comenzar Quiz'
          },
          wisdom: {
            title: 'Sabiduría del Bienestar',
            description: 'Descubre conocimiento sagrado del bienestar, perspectivas expertas y prácticas transformadoras para tu viaje',
            cta: 'Explorar Sabiduría'
          }
        }
      },

      // Sacred Moments Slider
      sacredMoments: {
        title: 'Momentos Sagrados',
        subtitle: 'Cada imagen lleva la esencia de la transformación',
        images: [
          {
            title: 'Masaje 5D',
            subtitle: 'El futuro de la relajación.',
            meaning: 'Experimenta la siguiente dimensión del toque sanador'
          },
          {
            title: 'Resplandor Lunar',
            subtitle: 'Un Rostro Que Brilla Como la Luna Llena.',
            meaning: 'Ilumina tu belleza natural desde adentro'
          },
          {
            title: 'Ritual Corporal Botánico Detox',
            subtitle: 'Ligero Como un Susurro.',
            meaning: 'Transformación suave que habla a tu alma'
          },
          {
            title: 'Transformación Profunda',
            subtitle: 'Bajo la Superficie, Todo Cambia.',
            meaning: 'La verdadera sanación comienza donde termina lo visible'
          },
          {
            title: 'Santuario de Terapia de Luz',
            subtitle: 'Desintoxica, relájate y potencia el bienestar con terapia infrarroja y luz roja.',
            meaning: 'Rejuvenecimiento celular a través de frecuencias de luz sagrada'
          },
          {
            title: 'Ritual Sagrado Diario',
            subtitle: 'Cada día es un gran día para un día de spa.',
            meaning: 'Abraza el bienestar como un estilo de vida'
          },
          {
            title: 'Sabiduría y Creación',
            subtitle: 'Vete con conocimiento experto y dos productos hechos por ti.',
            meaning: 'Empodera tu viaje con sanación hecha a mano'
          },
          {
            title: 'Despertar Celular',
            subtitle: 'Luz Que Despierta Tus Células.',
            meaning: 'Enciende la inteligencia sanadora natural de tu cuerpo'
          },
          {
            title: 'Renovación del Alma',
            subtitle: 'Como Mudar la Piel del Alma.',
            meaning: 'Libera lo que ya no sirve a tu ser superior'
          },
          {
            title: 'Ritual Madre e Hija',
            subtitle: 'Momentos Que Permanecen Para Siempre.',
            meaning: 'Vínculo sagrado a través de experiencias compartidas de bienestar'
          },
          {
            title: 'Sanación de Sonido Sagrado',
            subtitle: 'Om Shanti Shanti Shanti Om.',
            meaning: 'Encuentra paz a través de la sabiduría vibracional ancestral'
          },
          {
            title: 'Facial de Resplandor Interior',
            subtitle: 'Renueva tu piel desde adentro.',
            meaning: 'La verdadera belleza emerge de la armonía interior'
          },
          {
            title: 'Sanación Sónica',
            subtitle: 'El poder de los cuencos tibetanos en el cuerpo.',
            meaning: 'Frecuencias ancestrales que restauran el equilibrio energético'
          }
        ]
      },

      // The Ama Experience Section
      amaExperience: {
        title: 'La Experiencia Ama',
        subtitle: 'Donde el bienestar holístico se encuentra con la tecnología avanzada',
        philosophy: {
          text1: 'Descubre un espacio de bienestar holístico y tecnología avanzada, donde nuestra filosofía se basa en brindar relajación y alivio del estrés en tu vida diaria. Hemos elevado el concepto de bienestar incorporando tecnologías no invasivas en nuestros tratamientos, con terapias altamente efectivas que entregan resultados visibles desde la primera sesión.',
          text2: 'Una experiencia que transforma, renueva y deja beneficios duraderos tanto para el cuerpo como para la mente.'
        },
        imageGallery: {
          infraredTherapy: 'Terapia infrarroja y luz roja',
          treatmentRooms: 'Salas de Tratamiento',
          advancedTechnology: 'Tecnología Avanzada',
          swimmingPool: 'Piscina'
        },
        biohacking: {
          title: 'BIOHACKING Y TERAPÉUTICO',
          subtitle: 'EXPERIENCIAS',
          description: 'Rituales de bienestar avanzados que combinan tecnología, naturaleza y técnicas inteligentes para mejorar la energía, longevidad y equilibrio holístico.',
          services: [
            {
              title: 'Sauna Infrarroja de Espectro Completo',
              description: 'Terapia de calor penetrante profundo para desintoxicación, alivio del dolor y regeneración celular'
            },
            {
              title: 'Medición Bioenergética',
              description: 'Análisis avanzado de los campos energéticos y biorritmos de tu cuerpo para protocolos de bienestar personalizados'
            },
            {
              title: 'Presoterapia Body Ballancer',
              description: 'Terapia de compresión neumática para mejorar la circulación, drenaje linfático y recuperación'
            },
            {
              title: 'Terapia de Sonido Vibracional',
              description: 'Frecuencias terapéuticas y vibraciones para armonizar cuerpo, mente y sanación celular'
            }
          ]
        },
        ballancer: {
          title: '¿Qué distingue al Ballancer®Pro?',
          features: [
            'Líder global en el mercado desde hace 20 años',
            'Presión precisamente graduada y distribuida uniformemente',
            'Calibración exacta',
            'Máximo seguro de 80mmHg',
            'Respaldado por estudios clínicos',
            'Modo patentado de "pre-tratamiento" que prepara los ganglios linfáticos',
            'Aprobado por la FDA',
            'Sistema de Recuperación Corporal Completo'
          ]
        },
        bookNow: 'Reservar Ahora'
      },

      // Booking Modal
      bookingModal: {
        title: 'Reserva Tu Experiencia de Spa',
        sections: {
          treatment: {
            title: 'Seleccionar Tratamiento',
            label: 'Elige tu tratamiento',
            placeholder: 'Selecciona un tratamiento...',
            minutes: 'minutos'
          },
          datetime: {
            title: 'Fecha y Hora Preferida',
            dateLabel: 'Fecha Preferida',
            timeLabel: 'Hora Preferida',
            timePlaceholder: 'Selecciona la hora...',
            timeOptions: {
              '09:00': '9:00 AM',
              '10:00': '10:00 AM',
              '11:00': '11:00 AM',
              '12:00': '12:00 PM',
              '13:00': '1:00 PM',
              '14:00': '2:00 PM',
              '15:00': '3:00 PM',
              '16:00': '4:00 PM',
              '17:00': '5:00 PM',
              '18:00': '6:00 PM'
            }
          },
          contact: {
            title: 'Información de Contacto',
            nameLabel: 'Nombre Completo',
            emailLabel: 'Dirección de Email',
            phoneLabel: 'Número de Teléfono',
            requestsLabel: 'Solicitudes Especiales o Notas',
            requestsPlaceholder: 'Cualquier alergia, preferencia o requerimiento especial...',
            firstTimeLabel: 'Esta es mi primera visita a Ama Wellness & Spa'
          }
        },
        buttons: {
          submit: 'Solicitar Reserva',
          submitting: 'Enviando...',
          close: 'Cerrar'
        },
        validation: {
          requiredFields: '🌙 ¡Por favor completa todos los campos requeridos para tu viaje sagrado! 🌙',
          invalidEmail: '🌙 ¡Por favor ingresa una dirección de email válida para tu viaje de bienestar sagrado! 🌙'
        },
        messages: {
          success: '🌙✨ ¡Tu solicitud de reserva sagrada ha sido enviada! Recibirás un email de confirmación en breve y te contactaremos dentro de 24 horas para confirmar tu cita. ✨🌙',
          error: '💫 Las energías sagradas están temporalmente interrumpidas. ¡Por favor intenta de nuevo o llámanos directamente al +34 610 76 02 78! 💫'
        }
      },
      
      hero: {
        title: 'Redescubre Tu Resplandor Interior',
        subtitle: 'Donde la naturaleza, el bienestar y la tecnología se fusionan hermosamente',
        cta: 'Comienza Tu Viaje',
        toggleSound: 'Alternar sonidos ambientales'
      },
      
      // Treatments
      treatments: {
        title: 'Tratamientos Sagrados',
        subtitle: 'Cada tratamiento cuenta una historia de transformación',
        massage: {
          title: 'Terapia de Masajes',
          swedish: 'Masaje Sueco',
          deepTissue: 'Masaje de Tejido Profundo',
          aromatherapy: 'Masaje de Aromaterapia',
          hotStone: 'Masaje con Piedras Calientes',
          reflexology: 'Reflexología',
          indianHead: 'Masaje Indio de Cabeza'
        },
        facials: {
          title: 'Faciales y Belleza',
          tailored: 'Faciales Personalizados',
          antiAging: 'Tratamientos Anti-edad',
          exfoliation: 'Exfoliaciones Corporales Completas'
        },
        body: {
          title: 'Terapias Corporales y Rituales',
          wraps: 'Envolturas Corporales',
          balinese: 'Rituales Balineses',
          hammam: 'Rituales Hammam',
          wellness: 'Rituales de Bienestar'
        },
        other: {
          title: 'Otros Servicios',
          manicure: 'Manicura y Pedicura',
          waxing: 'Depilación',
          eyebrows: 'Servicios de Cejas y Pestañas'
        }
      },
      
      // Wellness Quiz
      quiz: {
        title: '¿Qué Anhela Tu Alma?',
        subtitle: 'Déjanos guiarte a tu tratamiento sagrado perfecto',
        resultsTitle: 'Tu Camino Sagrado Revelado',
        resultsSubtitle: 'Estos tratamientos han sido divinamente seleccionados para tu viaje',
        perfectTreatment: '✨ Tu Tratamiento Perfecto',
        alsoRecommended: '🌿 También Recomendado',
        bookTreatment: 'Reservar Este Tratamiento',
        takeAgain: 'Tomar Quiz Nuevamente',
        questions: {
          energy: '¿Cómo fluye tu energía hoy?',
          focusArea: '¿Dónde te gustaría enfocar tu sanación?',
          experienceType: '¿Qué tipo de experiencia sagrada te llama?'
        },
        options: {
          energy: {
            depleted: {
              title: 'Profundamente Agotado',
              description: 'Me siento drenado y necesito restauración completa'
            },
            stressed: {
              title: 'Estrés y Tensión',
              description: 'Mi cuerpo mantiene estrés, especialmente en cuello y hombros'
            },
            seekingBalance: {
              title: 'Buscando Equilibrio',
              description: 'Quiero armonizar y centrarme'
            },
            needDetox: {
              title: 'Necesito Renovación',
              description: 'Quiero limpiar y empezar de nuevo'
            }
          },
          focusArea: {
            fullBody: {
              title: 'Santuario de Cuerpo Completo',
              description: 'Relajación completa de pies a cabeza'
            },
            faceGlow: {
              title: 'Rostro Radiante',
              description: 'Renovación facial y piel brillante'
            },
            muscleRelief: {
              title: 'Alivio Muscular',
              description: 'Trabajo de tejido profundo para tensión y nudos'
            },
            mindSpirit: {
              title: 'Mente y Espíritu',
              description: 'Claridad mental y liberación emocional'
            }
          },
          experienceType: {
            traditional: {
              title: 'Sabiduría Ancestral',
              description: 'Masajes tradicionales y tratamientos naturales'
            },
            technology: {
              title: 'Biohacking Avanzado',
              description: 'Tecnología de vanguardia para bienestar óptimo'
            },
            ritual: {
              title: 'Rituales Sagrados',
              description: 'Ceremonias de múltiples pasos y tratamientos corporales'
            },
            quickEscape: {
              title: 'Renovación Express',
              description: 'Resultados poderosos en menos tiempo'
            }
          }
        }
      },

      // Contact
      contact: {
        hero: {
          title: 'Conecta Con Nosotros',
          subtitle: 'Comienza tu viaje sagrado hacia el bienestar y la transformación'
        },
        contactCards: {
          location: {
            title: 'Visita Nuestro Santuario',
            description: 'Disponible para citas y consultas'
          },
          phone: {
            title: 'Llámanos',
            description: 'Disponible para citas y consultas'
          },
          email: {
            title: 'Escríbenos',
            description: 'Responderemos en 24 horas'
          },
          instagram: {
            title: 'Sigue Nuestro Viaje',
            description: 'Inspiración diaria de bienestar y detrás de escena'
          }
        },
        map: {
          title: 'Encuentra Nuestro Santuario Sagrado'
        },
        form: {
          title: 'Envíanos un Mensaje',
          subtitle: 'Permítenos ayudarte a descubrir tu viaje de bienestar perfecto',
          labels: {
            name: 'Nombre *',
            email: 'Correo Electrónico *',
            phone: 'Teléfono',
            subject: 'Asunto',
            message: 'Mensaje *'
          },
          placeholders: {
            message: 'Cuéntanos cómo podemos ayudarte en tu viaje de bienestar...'
          },
          buttons: {
            send: 'Enviar Mensaje',
            sending: 'Enviando...'
          }
        },
        validation: {
          requiredFields: '🌙 ¡Por favor completa todos los campos requeridos para tu consulta sagrada! 🌙',
          invalidEmail: '🌙 ¡Por favor ingresa una dirección de correo válida para tu comunicación sagrada! 🌙',
          success: '🌙✨ ¡Tu mensaje sagrado ha sido enviado! Responderemos en 4 horas durante horario comercial. ¡Gracias por conectar con nuestro santuario de sanación! ✨🌙',
          error: '💫 ¡Las energías sagradas están temporalmente interrumpidas. Por favor intenta de nuevo o llámanos directamente al +34 610 76 02 78! 💫'
        }
      },

      // Luna Chatbot
      luna: {
        placeholder: "Pregunta a Luna sobre tu viaje sagrado de bienestar...",
        poweredBy: "Desarrollado por",
        clearChat: "Limpiar Chat",
        toggleChat: "Alternar Chat",
        errorMessage: "🌙 *Energía sagrada interrumpida...* ✨\n\nMi conexión cósmica al reino divino está temporalmente bloqueada, alma hermosa. ¡Por favor intenta de nuevo en un momento mientras realinéo mis frecuencias celestiales! 🔮💫",
        channeling: "🌙 Luna está canalizando sabiduría cósmica..."
      },
      
      // Common
      common: {
        loading: 'Cargando...',
        readMore: 'Leer Más',
        bookNow: 'Reservar Ahora',
        learnMore: 'Aprender Más',
        close: 'Cerrar',
        previous: 'Anterior',
        next: 'Siguiente',
        continue: 'Continuar',
        discoverPath: 'Descubrir Mi Camino',
        email: 'Correo',
        phone: 'Teléfono',
        name: 'Nombre',
        message: 'Mensaje',
        send: 'Enviar',
        subscribe: 'Suscribirse'
      },
      
      // Footer
      footer: {
        tagline: 'Un regreso a ti mismo',
        newsletter: 'Boletín Sagrado',
        newsletterDesc: 'Recibe sabiduría de bienestar y ofertas exclusivas',
        followUs: 'Síguenos',
        quickLinks: 'Enlaces Rápidos',
        treatments: 'Tratamientos',
        policies: 'Políticas',
        privacy: 'Política de Privacidad',
        terms: 'Términos de Servicio',
        copyright: '© 2025 Ama Wellness & Spa. Todos los derechos reservados.',
        madeWith: 'Hecho con ❤️ por EmerV🌸 @ <a href="https://infinia.ie" target="_blank" rel="noopener noreferrer">Infinia</a>'
      },

      // Treatments Page
      treatmentsPage: {
        title: 'Tratamientos Sagrados',
        subtitle: 'Relájate y renuévate con terapias holísticas y biohacking que reducen el estrés y mejoran cuerpo y mente desde la primera sesión.',
        categories: {
          all: 'Todos los Tratamientos',
          massages: 'Masajes Corporales',
          rituals: 'Rituales Corporales',
          facials: 'Faciales',
          packages: 'Rostro y Cuerpo',
          biohacking: 'Biohacking'
        },
        bookButton: 'Reservar Este Viaje'
      },

      // Blog Post Page
      blog: {
        backToBlog: 'Volver al Blog',
        bookingCTA: 'Reserva Tu Experiencia de Bienestar',
        relatedTopics: 'Temas Relacionados',
        postNotFound: {
          title: 'Artículo No Encontrado',
          message: 'El artículo del blog que buscas no existe.'
        }
      }
    }
  },
  
  fr: {
    translation: {
      // Navigation
      nav: {
        home: 'Accueil',
        treatments: 'Traitements',
        wellnessQuiz: 'Quiz Bien-être',
        aromatherapy: 'Aromathérapie',
        virtualEscape: 'Évasion Virtuelle',
        blog: 'Journal',
        booking: 'Réserver',
        about: 'À propos',
        contact: 'Contact'
      },
      
      // Homepage
      hero: {
        title: 'Redécouvrez Votre Éclat Intérieur',
        subtitle: 'Où la nature, le bien-être et la technologie fusionnent magnifiquement',
        cta: 'Commencez Votre Voyage',
        toggleSound: 'Basculer les sons ambiants'
      },
      
      // Treatments
      treatments: {
        title: 'Traitements Sacrés',
        subtitle: 'Chaque traitement raconte une histoire de transformation',
        massage: {
          title: 'Thérapie de Massage',
          swedish: 'Massage Suédois',
          deepTissue: 'Massage des Tissus Profonds',
          aromatherapy: 'Massage d\'Aromathérapie',
          hotStone: 'Massage aux Pierres Chaudes',
          reflexology: 'Réflexologie',
          indianHead: 'Massage Indien de la Tête'
        },
        facials: {
          title: 'Soins du Visage et Beauté',
          tailored: 'Soins du Visage Personnalisés',
          antiAging: 'Traitements Anti-âge',
          exfoliation: 'Exfoliations Corps Complet'
        },
        body: {
          title: 'Thérapies Corporelles et Rituels',
          wraps: 'Enveloppements Corporels',
          balinese: 'Rituels Balinais',
          hammam: 'Rituels Hammam',
          wellness: 'Rituels de Bien-être'
        },
        other: {
          title: 'Autres Services',
          manicure: 'Manucure et Pédicure',
          waxing: 'Épilation',
          eyebrows: 'Services Sourcils et Cils'
        }
      },
      
      // Wellness Quiz
      quiz: {
        title: 'De quoi votre corps a-t-il besoin aujourd\'hui?',
        subtitle: 'Laissez notre IA vous guider vers le traitement parfait',
        questions: {
          mood: 'Comment vous sentez-vous aujourd\'hui?',
          energy: 'Quel est votre niveau d\'énergie?',
          focus: 'Quelle zone a besoin d\'attention?',
          preference: 'Qu\'est-ce qui vous semble le plus attrayant?'
        },
        results: {
          title: 'Votre Recommandation Sacrée',
          treatment: 'Traitement Recommandé',
          oil: 'Huile Parfaite',
          affirmation: 'Affirmation Quotidienne',
          playlist: 'Playlist de Guérison'
        }
      },
      
      // Common
      common: {
        loading: 'Chargement...',
        readMore: 'Lire Plus',
        bookNow: 'Réserver Maintenant',
        learnMore: 'En Savoir Plus',
        close: 'Fermer',
        previous: 'Précédent',
        next: 'Suivant',
        email: 'Email',
        phone: 'Téléphone',
        name: 'Nom',
        message: 'Message',
        send: 'Envoyer',
        subscribe: 'S\'abonner'
      },
      
      // Footer
      footer: {
        tagline: 'Un retour à soi-même',
        newsletter: 'Newsletter Sacrée',
        newsletterDesc: 'Recevez la sagesse du bien-être et des offres exclusives',
        followUs: 'Suivez-nous',
        quickLinks: 'Liens Rapides',
        treatments: 'Traitements',
        policies: 'Politiques',
        privacy: 'Politique de Confidentialité',
        terms: 'Conditions de Service',
        copyright: '© 2025 Ama Wellness & Spa. Tous droits réservés.',
        madeWith: 'Fait avec ❤️ par EmerV🌸 @ <a href="https://infinia.ie" target="_blank" rel="noopener noreferrer">Infinia</a>'
      }
    }
  }
};

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: false,

    interpolation: {
      escapeValue: false // React already does escaping
    },

    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
    },

    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    }
  });

export default i18n; 
